import React, { useState, useMemo, useContext } from "react";
import useDashboardAnalytics from "../../services/Dashboard/useDashboardAnalytics";

import {
  BarChart3,
  PieChart as PieChartIcon,
  Download,
  IndianRupee,
  ShoppingCart,
  Crown,
  RefreshCcw,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from "recharts";
import { GlobalContext } from "../../Context/GlobalContext";
import { exportAnyJsonToExcel } from "../../utils/exportAnyJsonToExcel";
import { ToastContainer } from "react-toastify";

const COLORS = ["#6366F1", "#22C55E", "#F59E0B", "#EF4444", "#06B6D4"];

const currency = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const Reports = () => {
  // Default WhatsApp
  const [platform, setPlatform] = useState("whatsapp");
  const [interval, setInterval] = useState("30");
  const [refreshKey, setRefreshKey] = useState(0);

  const { userID } = useContext(GlobalContext);

  // Only selected platform API will call
  const { topOrdersAnalytics: finalData = [], loading } = useDashboardAnalytics(
    userID,
    interval,
    platform,
    {
      enableDashboard: false,
      enableOrderAnalytics: false,
      enableBestSelling: true,
    },
    refreshKey,
  );

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const totals = useMemo(() => {
    const revenue = finalData.reduce(
      (sum, item) => sum + Number(item.totalPrice || 0),
      0,
    );

    const orders = finalData.reduce(
      (sum, item) => sum + Number(item.orderCount || 0),
      0,
    );

    const top = [...finalData].sort(
      (a, b) => Number(b.orderCount) - Number(a.orderCount),
    )[0];

    return { revenue, orders, top };
  }, [finalData]);

  const chartData = finalData.map((item) => ({
    ...item,
    orderCount: Number(item.orderCount),
    totalPrice: Number(item.totalPrice),
  }));

  const exportExcel = () => {
    const cleanData = finalData.map((item) => ({
      Product: item.productName,
      Orders: Number(item.orderCount),
      Revenue: Number(item.totalPrice),
    }));

    exportAnyJsonToExcel({
      data: cleanData,
      filePrefix: "TopBestSellingOrders",
      sheetName: "OrdersReport",
    });
  };

  const noData = !loading && finalData.length === 0;

  return (
    <>
    <ToastContainer/>
    
    <div className="p-6 space-y-6 min-h-0 h-full flex flex-col overflow-hidden montserrat-font-1">
     <div className="bg-white border rounded-lg p-4 flex-shrink-0">
        {/* HEADER */}
        <div className="flex items-center justify-between montserrat-font-1 mb-4">
          <div className="text-xl font-semibold text-gray-800">
            Reports And Analytics
          </div>

          <div className="flex items-center gap-4 montserrat-font-1">
            {/* Platform */}
            <div className="flex flex-col text-xs text-gray-600">
              <label className="mb-1">Platform</label>

              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="border rounded-md px-3 py-1 text-sm bg-white"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="pos">POS</option>
              </select>
            </div>

            {/* Interval */}
            <div className="flex flex-col text-xs text-gray-600">
              <label className="mb-1">Interval</label>

              <select
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
                className="border rounded-md px-3 py-1 text-sm bg-white"
              >
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>

            <button
              onClick={exportExcel}
              className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 mt-5 rounded-md text-sm hover:bg-blue-700"
            >
              <Download size={16} />
              Export
            </button>

            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 mt-5 rounded-md text-sm hover:bg-green-700"
            >
              <span className={loading ? "animate-spin" : ""}>
                <RefreshCcw size={14} />
              </span>
              Refresh
            </button>
          </div>
        </div>

        {/* KPI CARDS */}
        <div className="flex items-center gap-2 mb-2 montserrat-font-1">
          <BarChart3 size={20} className="text-blue-600" />
          <h5 className="font-bold text-gray-800">Top Best-Selling Orders</h5>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-5 mb-3">
          <div className="bg-indigo-50 rounded-xl p-4">
            <div className="flex justify-between text-indigo-600 text-sm">
              Total Revenue
              <IndianRupee size={18} />
            </div>

            <div className="font-bold text-lg">{currency(totals.revenue)}</div>
          </div>

          <div className="bg-emerald-50 rounded-xl p-4">
            <div className="flex justify-between text-emerald-600 text-sm">
              Total Orders
              <ShoppingCart size={18} />
            </div>

            <div className="font-bold text-lg">{totals.orders}</div>
          </div>

          <div className="bg-orange-50 rounded-xl p-4">
            <div className="flex justify-between text-orange-600 text-sm">
              Top Product
              <Crown size={18} />
            </div>

            <div className="font-bold text-lg truncate">
              {totals.top?.productName || "-"}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 space-y-6 pr-2">
      {/* CHARTS */}

      <div className="grid md:grid-cols-2 gap-6 montserrat-font-1">
        {/* BAR CHART */}
        <div className="bg-white border rounded-xl p-4">
          {/* Header */}
          <div className="flex items-center gap-2 text-blue-600 font-semibold text-sm mb-3">
            <BarChart3 size={16} />
            <span>Orders by Product</span>
          </div>

          <div className="h-[240px]">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                Loading...
              </div>
            ) : noData ? (
              "No data"
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis
                    dataKey="productName"
                    tick={{ fontSize: 11 }}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                  />

                  <YAxis allowDecimals={false} />

                  <Tooltip />

                  <Bar dataKey="orderCount" radius={[6, 6, 0, 0]}>
                    <LabelList
                      dataKey="orderCount"
                      position="top"
                      style={{ fontSize: 12 }}
                    />

                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* PIE CHART */}
        <div className="bg-white border rounded-xl p-4 montserrat-font-1">
          {/* Header */}
          <div className="flex items-center gap-2 text-green-600 font-semibold text-sm mb-3">
            <PieChartIcon size={16} />
            <span>Revenue Share</span>
          </div>

          <div className="flex gap-4 h-[240px]">
            {/* PIE CHART */}
            <div className="w-1/2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="totalPrice"
                    nameKey="productName"
                    innerRadius={50}
                    outerRadius={80}
                  >
                    {chartData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>

                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* RIGHT SIDE LEGEND */}
            <div className="w-1/2 overflow-y-auto pr-2 montserrat-font-1">
              <div className="space-y-2 text-sm montserrat-font-1">
                {chartData.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between border-b pb-1"
                  >
                    <div className="flex items-center gap-2">
                      {/* COLOR DOT */}
                      <span
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: COLORS[i % COLORS.length] }}
                      />

                      {/* PRODUCT NAME */}
                      <span className="text-gray-700 truncate">
                        {item.productName}
                      </span>
                    </div>

                    {/* VALUE */}
                    <span className="font-medium text-gray-600">
                      ₹{Number(item.totalPrice)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}

      <div className="bg-white border rounded-lg h-full overflow-hidden">
        <div className="h-full overflow-auto">
          {/* Sticky Report Header */}
          <div className="sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-green-100 border-b">
            <span className="font-semibold text-gray-700">Detailed Report</span>

            <button
              onClick={exportExcel}
              className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700"
            >
              <Download size={16} />
              Export
            </button>
          </div>

          <div className="montserrat-font-1">
            <table className="w-full text-sm montserrat-font-1">
              <thead className="sticky top-[52px] z-20 text-gray-600">
                <tr>
                  <th className="bg-green-50 text-left px-4 py-3">Image</th>
                  <th className="bg-green-50 text-left px-4 py-3">Product</th>
                  <th className="bg-green-50 text-right px-4 py-3">Orders</th>
                  <th className="bg-green-50 text-right px-4 py-3">Revenue</th>
                </tr>
              </thead>

              <tbody>
                {finalData.map((item) => (
                  <tr
                    key={item.productId}
                    className="border-t hover:bg-green-50"
                  >
                    <td className="px-4 py-3">
                      <img
                        src={item.productImage || "/no-image.png"}
                        className="w-10 h-10 rounded object-cover"
                      />
                    </td>

                    <td className="px-4 py-3 font-medium">
                      {item.productName}
                    </td>

                    <td className="px-4 py-3 text-right">
                      {Number(item.orderCount)}
                    </td>

                    <td className="px-4 py-3 text-right">
                      {currency(Number(item.totalPrice))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
    </div>
    </>
  );
};

export default Reports;
